import { useEffect, useState } from "react";
import { api, HEADS, HEAD_COLORS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const KEY = {
  "Bed Roll": "bed_roll",
  "Coach Cleanliness": "coach_cleanliness",
  "Coach Maintenance": "coach_maintenance",
  "Water Availability": "water_availability",
};

export default function Settings() {
  const [t, setT] = useState(null);
  const [useOverride, setUseOverride] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/thresholds");
      setT(data);
      setUseOverride(!!data.global_override);
    })();
  }, []);

  if (!t) return null;

  const save = async () => {
    const v = Number(t.global_override);
    const payload = { ...t, global_override: useOverride ? (Number.isFinite(v) ? v : 7) : null };
    await api.put("/thresholds", payload);
    toast.success("Thresholds saved");
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Alert Thresholds</h1>
        <p className="text-sm text-slate-500 mt-1">A pair is flagged when complaints ≥ threshold on any head.</p>
      </div>

      <Card className="p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
          <div>
            <div className="font-semibold text-slate-800">Global override</div>
            <div className="text-xs text-slate-500">If enabled, applies one threshold across all 4 heads.</div>
          </div>
          <Switch data-testid="global-override-switch" checked={useOverride} onCheckedChange={setUseOverride} />
        </div>

        {useOverride ? (
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider text-slate-500">Global threshold</Label>
            <Input
              data-testid="global-threshold-input"
              type="number"
              value={t.global_override ?? 7}
              onChange={(e) => setT({ ...t, global_override: Number(e.target.value) })}
              className="h-9 mt-1 font-mono w-40"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {HEADS.map((h) => (
              <div key={h} className="p-3 rounded-lg border" style={{ background: HEAD_COLORS[h].bg }}>
                <Label className="text-xs uppercase font-semibold tracking-wider" style={{ color: HEAD_COLORS[h].text }}>{h}</Label>
                <Input
                  data-testid={`threshold-input-${KEY[h]}`}
                  type="number"
                  value={t[KEY[h]] ?? 7}
                  onChange={(e) => setT({ ...t, [KEY[h]]: Number(e.target.value) })}
                  className="h-9 mt-2 font-mono"
                />
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Button data-testid="save-thresholds-btn" className="bg-blue-600 hover:bg-blue-500 text-white" onClick={save}>
            Save Thresholds
          </Button>
        </div>
      </Card>
    </div>
  );
}
